import { useState, useRef, useEffect, useCallback, type ReactNode, type MouseEvent } from 'react';

/**
 * Tooltip partagé — issue #583
 *
 * Pattern : hover rapide + clic épingle + scrollable + Échap/clic-dehors pour
 * fermer. Réservé aux contenus longs (instructions LLM, métriques détaillées).
 *
 * Pour les contenus courts (1 phrase), utiliser `title=` HTML natif.
 *
 * Usage :
 *   <Tooltip content="Instructions LLM longues...">
 *     <InfoIcon />
 *   </Tooltip>
 *
 *   <Tooltip content={<div>...</div>} pinnable maxWidth="max-w-2xl">
 *     <span>Plus d'infos</span>
 *   </Tooltip>
 */

interface TooltipProps {
  /** Contenu du tooltip (string ou JSX). */
  content: ReactNode;
  /** Élément déclencheur (icône, bouton, texte). */
  children: ReactNode;
  /** Permettre le clic-épinglage. Défaut : true. */
  pinnable?: boolean;
  /** Classe Tailwind de largeur max. Défaut : `max-w-md`. */
  maxWidth?: string;
  /** Classe Tailwind de hauteur max + scroll vertical si nécessaire. Défaut : `max-h-[32rem]`. */
  maxHeight?: string;
  /** Placement vertical. Défaut : `bottom`. */
  placement?: 'top' | 'bottom';
  /**
   * Alignement horizontal du tooltip par rapport au déclencheur.
   * - `'left'` (défaut) : bord gauche du tooltip aligné au bord gauche du déclencheur (utile en début de ligne).
   * - `'right'` : bord droit du tooltip aligné au bord droit du déclencheur (utile en fin de ligne, pour éviter le débordement à droite de l'écran).
   */
  align?: 'left' | 'right';
  /** Pixels entre déclencheur et tooltip. Défaut : 5. */
  offset?: number;
  /** Titre affiché en en-tête du tooltip (optionnel, texte simple). */
  heading?: string;
  /** En-tête JSX riche (prioritaire sur `heading`) — pastille, badges, etc. */
  headingNode?: ReactNode;
  /** Hint affiché à droite de l'en-tête quand non-épinglé. Défaut : "Cliquer pour épingler". */
  pinHint?: string;
  /** Aria-label sur l'élément déclencheur. */
  ariaLabel?: string;
  /** Classes additionnelles sur l'élément déclencheur. */
  className?: string;
}

export function Tooltip({
  content,
  children,
  pinnable = true,
  maxWidth = 'max-w-md',
  maxHeight = 'max-h-[32rem]',
  placement = 'bottom',
  align = 'left',
  offset = 5,
  heading,
  headingNode,
  pinHint = 'Cliquer pour épingler',
  ariaLabel,
  className,
}: TooltipProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [position, setPosition] = useState<
    {
      top?: number;
      bottom?: number;
      left?: number;
      right?: number;
      effectivePlacement: 'top' | 'bottom';
      maxHeightPx?: number;
    } | null
  >(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const isOpen = isPinned || isHovered;

  // Calcule position + auto-flip vertical/horizontal selon l'espace disponible
  // dans le viewport. Si la taille naturelle du tooltip est connue (après 1er
  // rendu), on tient compte de sa hauteur réelle pour décider du flip.
  const computePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    const margin = 8; // marge de sécurité avec les bords du viewport

    // Espace disponible au-dessus et en-dessous du déclencheur
    const spaceBelow = viewportH - rect.bottom - offset - margin;
    const spaceAbove = rect.top - offset - margin;

    // Hauteur naturelle du tooltip (si déjà rendu, sinon estimation)
    const tooltipH = tooltipRef.current?.offsetHeight ?? 200;

    // Décision verticale : on respecte le placement demandé sauf si le tooltip
    // ne tient pas. Si aucun côté ne suffit, on choisit le plus grand des deux
    // et on contraindra la hauteur via maxHeightPx.
    let effectivePlacement: 'top' | 'bottom' = placement;
    if (placement === 'bottom' && tooltipH > spaceBelow && spaceAbove > spaceBelow) {
      effectivePlacement = 'top';
    } else if (placement === 'top' && tooltipH > spaceAbove && spaceBelow > spaceAbove) {
      effectivePlacement = 'bottom';
    }

    const availableSpace = effectivePlacement === 'top' ? spaceAbove : spaceBelow;
    const maxHeightPx = availableSpace > 0 ? availableSpace : undefined;

    // Décision horizontale : on respecte align mais on flippe si le tooltip
    // déborderait du viewport. Estimation largeur si pas encore rendu.
    const tooltipW = tooltipRef.current?.offsetWidth ?? 320;
    let horizontal: { left?: number; right?: number };
    if (align === 'right') {
      const wouldOverflowLeft = rect.right - tooltipW < margin;
      if (wouldOverflowLeft && rect.left + tooltipW + margin <= viewportW) {
        horizontal = { left: rect.left };
      } else {
        horizontal = { right: Math.max(margin, viewportW - rect.right) };
      }
    } else {
      const wouldOverflowRight = rect.left + tooltipW > viewportW - margin;
      if (wouldOverflowRight && rect.right - tooltipW >= margin) {
        horizontal = { right: viewportW - rect.right };
      } else {
        horizontal = { left: Math.max(margin, rect.left) };
      }
    }

    if (effectivePlacement === 'top') {
      setPosition({
        bottom: viewportH - rect.top + offset,
        ...horizontal,
        effectivePlacement,
        maxHeightPx,
      });
    } else {
      setPosition({
        top: rect.bottom + offset,
        ...horizontal,
        effectivePlacement,
        maxHeightPx,
      });
    }
  }, [placement, offset, align]);

  const handleMouseEnter = useCallback(() => {
    if (isPinned) return;
    computePosition();
    setIsHovered(true);
  }, [isPinned, computePosition]);

  const handleMouseLeave = useCallback(() => {
    if (isPinned) return;
    setIsHovered(false);
  }, [isPinned]);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLSpanElement>) => {
      if (!pinnable) return;
      event.stopPropagation();
      if (isPinned) {
        setIsPinned(false);
        setIsHovered(false);
        return;
      }
      computePosition();
      setIsPinned(true);
      setIsHovered(true);
    },
    [pinnable, isPinned, computePosition],
  );

  const close = useCallback(() => {
    setIsPinned(false);
    setIsHovered(false);
  }, []);

  // Après le 1er render, on connaît la taille réelle du tooltip — on recalcule
  // au cas où on aurait pris la mauvaise décision de placement (estimation
  // initiale 200x320). Aussi : re-calcul sur resize/scroll de la fenêtre.
  useEffect(() => {
    if (!isOpen) return;
    // Premier recalcul juste après mount, avec la vraie taille
    const id = requestAnimationFrame(() => computePosition());
    const onResize = () => computePosition();
    const onScroll = () => computePosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [isOpen, computePosition]);

  useEffect(() => {
    if (!isPinned) return;
    const handleClickOutside = (e: globalThis.MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isPinned, close]);

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        aria-label={ariaLabel}
        className={`inline-flex items-center ${pinnable ? 'cursor-pointer' : 'cursor-help'} ${className ?? ''}`}
      >
        {children}
      </span>
      {isOpen && position && (
        <div
          ref={tooltipRef}
          role="tooltip"
          style={{
            position: 'fixed',
            top: position.top,
            bottom: position.bottom,
            left: position.left,
            right: position.right,
            maxHeight: position.maxHeightPx ? `${position.maxHeightPx}px` : undefined,
          }}
          className={`z-50 bg-gray-900 text-white text-sm rounded-lg shadow-lg p-3 ${maxWidth} ${position.maxHeightPx ? '' : maxHeight} overflow-y-auto`}
        >
          {(heading || headingNode || pinnable) && (
            <div className="flex items-center justify-between mb-1 gap-3 sticky top-0 bg-gray-900 -mx-3 px-3 pb-1 border-b border-gray-700">
              {headingNode
                ? <div>{headingNode}</div>
                : heading && <div className="font-semibold text-blue-300">{heading}</div>}
              {pinnable &&
                (isPinned ? (
                  <button
                    type="button"
                    onClick={close}
                    className="text-gray-400 hover:text-white text-base leading-none px-2"
                    aria-label="Fermer"
                    title={"Fermer (Échap)"}
                  >
                    {"×"}
                  </button>
                ) : (
                  <span
                    className="text-gray-400 inline-flex items-center"
                    title={pinHint}
                    aria-label={pinHint}
                  >
                    <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z" />
                    </svg>
                  </span>
                ))}
            </div>
          )}
          <div className={`whitespace-pre-wrap ${heading || pinnable ? 'pt-2' : ''}`}>{content}</div>
        </div>
      )}
    </>
  );
}

export default Tooltip;
